import React, {useState} from 'react'
import { Helmet } from 'react-helmet';
import { Container, Row, Col, Button, Card, Nav, Table } from "react-bootstrap";
import { Link } from 'react-router-dom';

import './cij.css'
import './zartecbutton.css'
import img2 from '../Components/Images/TTO/TTO1-1.png'
import img3 from '../Components/Images/TTO/TTO1-2.png'
import img4 from '../Components/Images/TTO/TTO1-3.png'
import img5 from '../Components/Images/TTO/TTO1-4.png'
import img6 from '../Components/Images/applications/tto-1.png'
import img7 from '../Components/Images/applications/tto-2.png'
import img8 from '../Components/Images/applications/tto-3.png'
import { Bold } from 'lucide-react';
import { useRef } from 'react';

function TTO32I() {
    const secondSectionRef = useRef(null);

    const scrollToSection = () => {
    secondSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

    const [activeTab, setActiveTab] = useState('specifications');

    // SEO Metadata
    const seoData = {
        title: "SAVEMA TTO Printers | Thermal Transfer Overprinters for Packaging",
        description: "High-quality SAVEMA TTO printers for date coding, batch coding, and variable printing on sachets, pouches, and flexible packaging. Ribbon-saving thermal transfer solutions for pharmaceutical, food, and cosmetic industries.",
        keywords: `Savema TTO printers, Smart date coder, thermal ribbon printer, TTO batch coding printer, 
            Ribbon saving coding solutions, thermal printer for sachets and stick packs, 
            Cosmetic and beauty packaging printer, best thermal transfer over printer, 
            Pharmaceutical sachet coding, date and batch code printer for pouches,
            flexible packaging printer, industrial thermal transfer printer`,
        canonicalUrl: "https://zartecsolutions.com/thermal-transfer-printer/32I&C-TTO"
    };

    const productsI9 = [
        {
            id: 1,
            model: "32 x 40I - Magnetic Printer",
            description: "Compact design TTO printer with IP65 protection and advanced ink modulation for consistent printing on flexible packaging.",
            image: img2,
            alt: "SAVEMA 32x40I magnetic thermal transfer printer"
        },
        {
            id: 2,
            model: "32 x 50I TTO Printer",
            description: "Enhanced ribbon-saving TTO printer with IP65 protection and user-friendly interface for batch coding applications.",
            image: img3,
            alt: "SAVEMA 32x50I thermal transfer overprinter"
        },
        {
            id: 3,
            model: "32 x 70I TTO Printer",
            description: "Advanced TTO printer with twin-valve technology and auto-cleaning system for pharmaceutical sachet coding.",
            image: img4,
            alt: "SAVEMA 32x70I thermal transfer printer for flexible packaging"
        },
        {
            id: 4,
            model: "32 C LH or RH TTO Printer",
            description: "Versatile TTO printer with 125mm print area for continuous coding on pouches and stick packs.",
            image: img5,
            alt: "SAVEMA 32C left/right hand thermal transfer printer"
        }
    ];

    const images = [
        { id: 1, src: img6, alt: 'TTO printer coding on cables and wires' },
        { id: 2, src: img7, alt: 'Thermal transfer printing on metal packaging' },
        { id: 3, src: img8, alt: 'Date coding on plastic pipes with TTO printer' },
    ];

    const featuresContent = (
        <div style={{paddingLeft:"20px", paddingTop:"10px", paddingBottom:"10px"}}>
            <h2 className="h4" style={{marginTop:"10px"}}>Technical Specifications</h2>
            <ul className='p-3' style={{ listStyle: "none", paddingLeft: 0 }}>
                {[
                    "32mm x 125mm Continuous mode print area for flexible packaging",
                    "600 meter ribbon capacity for extended operation",
                    "High resolution 300 dpi (12 dots/mm) for crisp text and barcodes",                         
                    "Print Speed 10-550 mm/sec for various production line speeds",                        
                    "4.3\" touch panel color control screen for easy operation",                       
                    "90°, 180°, 270° rotation features for versatile printing positions", 
                    "Memory: 5000 templates storage capacity",
                    "Power Supply: 100/240V 50/60 Hz universal input", 
                    "Programming: Real Date/Time, Text, 1D/2D Barcodes, Logos, Counters", 
                    "Barcode Types: EAN-13, EAN-8, CODE 128, QR Code, Datamatrix, PDF417",
                    "Communication: RS-232, Ethernet, USB for flexible connectivity",
                    "Free PC Software: Sayasis S20 with all features included"
                ].map((item, index) => (
                    <li key={index} style={{ position: "relative", paddingLeft: "25px", marginBottom: "8px" }}>
                        <span style={{ 
                            position: "absolute", 
                            left: 0,
                            color: "#00663b",
                            fontWeight: "bold",
                            fontSize:"17px",
                        }}>*</span>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );

    const applicationsContent = (
        <div style={{marginLeft:"10px", marginRight:"10px"}} className='pt-5'>
            <h2 className="text-center mb-4" style={{fontSize:"25px", fontFamily:"sans-serif"}}>TTO Printer Applications</h2>
            <Row className="g-4 mb-5">
                {images.map((image) => (
                    <Col key={image.id} xs={12} sm={6} md={4}>
                        <div className="image-card">
                            <img 
                                src={image.src} 
                                alt={image.alt}
                                className="img-fluid rounded"
                                style={{maxHeight:"300px"}}
                                loading="lazy"
                            />
                            <div className="image-overlay">
                                <p className="image-caption">{image.alt}</p>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        </div>
    );
    
    const technicalContent = (
        <div style={{paddingLeft:"20px", paddingTop:"10px", paddingBottom:"10px"}}>
            <h2 className="h4" style={{marginTop:"10px"}}>Key Features of SAVEMA TTO Printers</h2>
            <div>
                <p className='p-3 text-justify'>
                The high-performance SAVEMA Thermal Transfer Overprinters (TTOs) provide cost-effective coding solutions for printing fixed and variable text, data, and graphics on flexible packaging. Ideal for barcodes, real-time dates, batch numbers, and source codes, these printers are perfect for pharmaceutical sachets, food pouches, and cosmetic packaging. Available in both intermittent and continuous models, they easily integrate with vertical/horizontal form-fill-seal systems, labelers, and overwrapping equipment.
                </p>
               
                <ul className='p-3' style={{ listStyle: "none", paddingLeft: 0 }}>
                    {[
                        "Robust mechanical design requires minimal maintenance",
                        "32mm – 40mm print area (32 x 40I Magnetic Printer)",
                        "32mm – 50mm print area (32 x 50I TTO Printer)",
                        "32mm – 76mm print area (32 x 70I TTO Printer)",
                        "32mm – 125mm print area (32 C LH/RH TTO Printer)",
                        "600m ribbon capacity reduces changeover frequency",
                        "300 dpi (12 dots/mm) print quality for sharp codes",
                        "Up to 300mm/sec print speed for high-speed lines",
                        "Free Sayasis S20 template design software",
                        "Large onboard memory for label storage",
                        "USB transfer for easy message management"
                    ].map((item, index) => (
                        <li key={index} style={{ position: "relative", paddingLeft: "25px", marginBottom: "8px" }}>
                            <span style={{ 
                                position: "absolute", 
                                left: 0,
                                color: "#00663b",
                                fontWeight: "bold",
                                fontSize:"17px",
                            }}>•</span>
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
    
    const renderTabContent = () => {
        switch (activeTab) {
            case 'specifications':
                return technicalContent;
            case 'features':
                return featuresContent;
            case 'applications':
                return applicationsContent;
            default:
                return null;
        }
    };
    
    return (
        <div>
            <Helmet>
                <title>{seoData.title}</title>
                <meta name="description" content={seoData.description} />
                <meta name="keywords" content={seoData.keywords} />
                <link rel="canonical" href={seoData.canonicalUrl} />
                
                {/* Open Graph / Social Media Meta Tags */}
                <meta property="og:title" content={seoData.title} />
                <meta property="og:description" content={seoData.description} />
                <meta property="og:url" content={seoData.canonicalUrl} />
                <meta property="og:type" content="product" />
                
                {/* Twitter Card Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoData.title} />
                <meta name="twitter:description" content={seoData.description} />

                {/* Schema.org markup for Google */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "ProductCollection",
                        "name": "SAVEMA TTO Printers",
                        "description": seoData.description,
                        "keywords": seoData.keywords,
                        "hasOfferCatalog": {
                            "@type": "OfferCatalog",
                            "name": "Thermal Transfer Printers",
                            "itemListElement": productsI9.map(product => ({
                                "@type": "Offer",
                                "itemOffered": {
                                    "@type": "Product",
                                    "name": product.model,
                                    "description": product.description,
                                    "image": product.image
                                }
                            }))
                        }
                    })}
                </script>
            </Helmet>

            <section>
                <Container className="py-5">
                    <h1 className="mb-4 cijh2" style={{fontSize:"30px"}}>SAVEMA 32I and C Series Thermal Transfer Printers</h1>
                    <Row>
                        {productsI9.map(product => (
                            <Col lg={4} md={6} className="mb-4" key={product.id}>
                                <Card className="h-100 border-light shadow-sm rounded-3">
                                    <Card.Body className="d-flex flex-column">
                                        <div className="mb-2" 
                                        style={{
                                            background: "linear-gradient(135deg, #0A6E54 0%, #1B4B89 100%)",
                                            WebkitBackgroundClip: "text",
                                            backgroundClip: "text",
                                            color: "transparent",
                                            fontWeight:"bold",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            textDecoration: "none",
                                            marginTop: "auto",
                                        }}>
                                            {product.model}
                                        </div>
                                        <p className="text-muted">{product.description}</p>
                                        <button 
                                            className={`zartec-btn zartec-btn-primary zartec-btn-small`}
                                            onClick={scrollToSection}
                                        >
                                            Get Product Details
                                        </button>
                                    </Card.Body>
                                    <div className="p-3 text-center">
                                        <img 
                                            src={product.image} 
                                            alt={product.alt} 
                                            className="img-fluid" 
                                            style={{ maxHeight: '200px' }}
                                            loading="lazy"
                                        />
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>
            
            <section className='sect2' ref={secondSectionRef}>
                <div className="product-navbar-container mb-4">
                    <Nav className="nav-tabs-custom d-flex justify-content-center">
                        <Nav.Item className="flex-fill text-center">
                            <Nav.Link 
                                className={`px-4 py-3 ${activeTab === 'specifications' ? 'active' : ''}`}
                                onClick={() => setActiveTab('specifications')}
                            >
                                <span className="nav-text">Key Features</span>
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item className="flex-fill text-center">
                            <Nav.Link 
                                className={`px-4 py-3 ${activeTab === 'features' ? 'active' : ''}`}
                                onClick={() => setActiveTab('features')}
                            >
                                <span className="nav-text">Technical Specifications</span>
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item className="flex-fill text-center">
                            <Nav.Link 
                                className={`px-4 py-3 ${activeTab === 'applications' ? 'active' : ''}`}
                                onClick={() => setActiveTab('applications')}
                            >
                                <span className="nav-text">Applications</span>
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                </div>
                <div className="border border-top-0 p-0 bg-white">
                    {renderTabContent()}
                </div>
            </section>
        </div>
    );
}

export default TTO32I;